/**
 * Agents Service
 * Manages agent-related API operations
 */

import { TeamScopedService } from './base.service';
import type { Agent } from '@zenmako/contracts/agents';
import type { Connection } from '../types';

export interface ListAgentsResponse {
  agents: Agent[];
  total?: number;
}

export interface GetAgentResponse {
  agent: Agent;
}

export interface GetAgentConnectionsResponse {
  connections: Connection[];
}

class AgentsService extends TeamScopedService {
  /**
   * Get all agents for a team
   */
  async getAgents(teamId: string): Promise<Agent[]> {
    const response = await this.getTeam<ListAgentsResponse>(teamId, '/agents');
    return response.agents || [];
  }

  /**
   * Get a specific agent by ID
   */
  async getAgent(teamId: string, agentId: string): Promise<Agent> {
    const response = await this.getTeam<GetAgentResponse>(teamId, `/agents/${agentId}`);
    return response.agent;
  }

  /**
   * Get connections available to an agent
   */
  async getAgentConnections(teamId: string, agentId: string): Promise<Connection[]> {
    const response = await this.getTeam<GetAgentConnectionsResponse>(
      teamId,
      `/agents/${agentId}/connections`
    );
    return response.connections || [];
  }

  /**
   * Create a new agent (future)
   */
  async createAgent(teamId: string, agent: Partial<Agent>): Promise<Agent> {
    const response = await this.postTeam<GetAgentResponse>(teamId, '/agents', agent);
    return response.agent;
  }

  /**
   * Update an agent (future)
   */
  async updateAgent(teamId: string, agentId: string, updates: Partial<Agent>): Promise<Agent> {
    const response = await this.patchTeam<GetAgentResponse>(
      teamId,
      `/agents/${agentId}`,
      updates
    );
    return response.agent;
  }

  /**
   * Delete an agent (future)
   */
  async deleteAgent(teamId: string, agentId: string): Promise<void> {
    await this.deleteTeam(teamId, `/agents/${agentId}`);
  }
}

export const agentsService = new AgentsService();
